/**
 * Neue Artikel aus Belegen entscheiden — einmal je Lieferant und Artikel.
 *
 * Benjamin, 25.09.2026: „möglichst wenig und mit zunehmend gepflegtem Datensatz
 * immer weniger Arbeit". Deshalb steht hier nur, was Kontor nicht selbst
 * zuordnen konnte (lieferant_artikel.abgleichen), und jede Antwort gilt für
 * alle Belege mit demselben Artikel — jetzt und künftig:
 *   Aufnehmen          → neues Material bzw. neue Maschine im Katalog, beim Lieferanten gelistet
 *   = Vorschlag        → ist dasselbe wie ein vorhandenes (Beinahe-Treffer, ein Klick)
 *   Anderes …          → ist dasselbe wie … (Suche im Katalog)
 *   Ignorieren         → gehört nicht auf die Liste (wird nie wieder gefragt)
 * „Alle aufnehmen" nimmt den ganzen Rest in einem Zug.
 */
import { useState } from "react";
import type { ReactElement } from "react";
import { fehlerText } from "@vimaana/ui";

import MaterialWahl from "./MaterialWahl";
import { useArtikelEntscheiden } from "../../hooks/useArtikel";
import type { ArtikelEntscheidung, ArtikelOffen } from "../../hooks/useArtikel";
import "../../styles/lieferanten.css";

const eur = (n: number) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const schl = (z: ArtikelOffen) => `${z.firma_id}\t${z.schluessel}`;

export function ArtikelPruefen({ zeilen, mitFirma = false }: { zeilen: ArtikelOffen[]; mitFirma?: boolean }): ReactElement | null {
  const entscheiden = useArtikelEntscheiden();
  const [anders, setAnders] = useState<string | null>(null);
  const [arten, setArten] = useState<Record<string, string>>({});
  const [erledigt, setErledigt] = useState<Set<string>>(new Set());

  const sichtbar = zeilen.filter((z) => !erledigt.has(schl(z)));
  if (sichtbar.length === 0) return null;

  const art = (z: ArtikelOffen) => arten[schl(z)] || (z.art === "maschine" ? "maschine" : "material");
  const tun = (e: ArtikelEntscheidung[]) => {
    // Sofort aus der Liste — die Antwort des Servers lädt ohnehin nach.
    setErledigt((alt) => new Set([...alt, ...e.map((x) => `${x.firma_id}\t${x.schluessel}`)]));
    entscheiden.mutate(e, {
      onError: () => setErledigt((alt) => {
        const neu = new Set(alt);
        e.forEach((x) => neu.delete(`${x.firma_id}\t${x.schluessel}`));
        return neu;
      }),
    });
  };
  const basis = (z: ArtikelOffen) => ({ firma_id: z.firma_id, schluessel: z.schluessel });

  const gruppen: { firma: string; zeilen: ArtikelOffen[] }[] = [];
  for (const z of sichtbar) {
    const g = mitFirma ? gruppen.find((x) => x.firma === z.firma) : gruppen[0];
    if (g) g.zeilen.push(z);
    else gruppen.push({ firma: mitFirma ? z.firma : "", zeilen: [z] });
  }

  return (
    <div className="ap">
      <div className="ap-kopf">
        <span>
          <strong>{sichtbar.length}</strong> {sichtbar.length === 1 ? "neuer Artikel" : "neue Artikel"} — einmal entscheiden,
          danach erkennt Kontor {sichtbar.length === 1 ? "ihn" : "sie"} selbst.
        </span>
        <button
          type="button"
          className="lf-knopf is-primaer"
          disabled={entscheiden.isPending}
          onClick={() => tun(sichtbar.map((z) => ({ ...basis(z), aktion: "aufnehmen", art: art(z) })))}
        >
          {sichtbar.length === 1 ? "Aufnehmen" : `Alle ${sichtbar.length} aufnehmen`}
        </button>
      </div>
      {entscheiden.error && <div className="lf-fehler">{fehlerText(entscheiden.error)}</div>}
      {gruppen.map((g) => (
        <section key={g.firma || "-"} className="ap-gruppe">
          {mitFirma && <h4 className="ap-firma">{g.firma} <span>{g.zeilen.length}</span></h4>}
          <ul className="ap-liste">
            {g.zeilen.map((z) => (
              <li key={schl(z)} className="ap-zeile">
                <div className="ap-haupt">
                  <span className="ap-name">{z.bezeichnung}</span>
                  <span className="ap-info">
                    {[z.artikelnummer ? `Art.-Nr. ${z.artikelnummer}` : "",
                      z.preis ? `${eur(z.preis)} netto${z.einheit ? ` / ${z.einheit}` : ""}` : z.einheit,
                      z.belege > 1 ? `auf ${z.belege} Belegen` : "",
                      z.lieferscheine ? (z.lieferscheine === z.belege ? "vom Lieferschein" : `${z.lieferscheine} davon Lieferschein`) : ""]
                      .filter(Boolean).join(" · ")}
                  </span>
                </div>
                <div className="ap-tat">
                  <button
                    type="button"
                    className={"ap-art" + (art(z) === "maschine" ? " ist-maschine" : "")}
                    title="Umschalten: Material oder Maschine"
                    onClick={() => setArten((a) => ({ ...a, [schl(z)]: art(z) === "maschine" ? "material" : "maschine" }))}
                  >
                    {art(z) === "maschine" ? "Maschine" : "Material"}
                  </button>
                  <button type="button" className="lf-knopf is-primaer" disabled={entscheiden.isPending}
                    onClick={() => tun([{ ...basis(z), aktion: "aufnehmen", art: art(z) }])}>
                    Aufnehmen
                  </button>
                  {z.vorschlag && (
                    <button type="button" className="lf-knopf" disabled={entscheiden.isPending} title="Ist dasselbe wie dieses Material"
                      onClick={() => tun([{ ...basis(z), aktion: "zuordnen", material_id: z.vorschlag!.id }])}>
                      = {z.vorschlag.name}
                    </button>
                  )}
                  <button type="button" className="lf-knopf" onClick={() => setAnders(anders === schl(z) ? null : schl(z))}>
                    Anderes …
                  </button>
                  <button type="button" className="lf-knopf ap-leise" disabled={entscheiden.isPending}
                    title="Gehört nicht auf die Liste — wird bei diesem Lieferanten nie wieder gefragt"
                    onClick={() => tun([{ ...basis(z), aktion: "ignorieren" }])}>
                    Ignorieren
                  </button>
                </div>
                {anders === schl(z) && (
                  <div className="ap-anders">
                    <MaterialWahl
                      platzhalter="Ist dasselbe wie … (im Katalog suchen)"
                      onAbbruch={() => setAnders(null)}
                      onWahl={(m) => { setAnders(null); tun([{ ...basis(z), aktion: "zuordnen", material_id: m.id }]); }}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
